import type { AppUser } from '@/api/models/AppUser';
import type { OrderSettingsDropdown, SaveOrderSettingsPayload } from '@/api/models/OrderSettings';
import * as API from '@/api/endpoints';
import type { OrdersPageType } from '@/components/Orders/Dashboard/OrdersPresentation';
import { NotificationEventTypeDB } from '@/db/repositories/NotificationEventTypeRepo';
import {
  getOrderNotificationOptions,
  getOrderRefreshRate,
  type OrderRefreshRate,
  getOrderNotificationTypeIds,
  orderSettingsKeys,
  parseOrderSearchDefaults,
  serializeOrderSearchDefaults,
  type OrderSearchDefaults,
} from '@/library/OrderSettingsHelper';
import { getUser, setUser, updateOrderSettings } from '@/library/UserSessionHelper';
import { syncOrderNotificationEvents } from './OrderStaticService';
import { loadOrderFilterOptions, type OrderFilterOptions } from '@/library/OrderFilterOptionsHelper';
import { onlineStatus } from '@/utils/onlineStatus';

export interface OrderSettingsDraft {
  appUserId: number;
  page: OrdersPageType;
  appUserSetting: SaveOrderSettingsPayload['appUserSetting'];
  notificationTypeId: number;
  options: OrderSettingsDropdown[];
  selectedIds: number[];
  searchDefaults: OrderSearchDefaults;
  refreshRate: OrderRefreshRate;
  filterOptions: OrderFilterOptions;
}

export const cacheUserSettings = async (appUser: AppUser): Promise<void> => {
  const user = await getUser();
  if (!user || user.appUserId !== appUser.appUserId) return;
  user.orderSettings = appUser.appUserSettingData ?? null;
  await setUser(user);
};

export const loadOrderSettings = async (page: OrdersPageType): Promise<OrderSettingsDraft> => {
  const user = await getUser();
  if (!user?.appUserId) throw new Error('Please sign in again to load your settings.');
  const source = user.orderSettings?.[orderSettingsKeys[page]];
  const setting = source?.appUserSetting;
  const isNewSetting = setting?.appUserSettingId === 0 && setting.appUserId === 0;
  if (!source || !setting || setting.appSectionId <= 0 || source.notificationTypeId <= 0 || (!isNewSetting && setting.appUserId !== user.appUserId)) {
    throw new Error(`Your ${page} settings are unavailable. Please sign in again.`);
  }

  if (onlineStatus.get()) {
    try {
      await syncOrderNotificationEvents(getOrderNotificationTypeIds(user.orderSettings));
    } catch (error) {
      // Cached lookup data can still support viewing and saving after a refresh fails.
      console.error('Could not refresh order settings lookups:', error);
    }
  }
  const notificationTypeId = source.notificationTypeId;
  const options = getOrderNotificationOptions(await NotificationEventTypeDB.list(notificationTypeId));
  const selectedIds = [...(source.appUserEventIds ?? [])];
  const appUserSetting = {
    appUserSettingId: setting.appUserSettingId,
    appUserId: user.appUserId,
    createAppUserId: user.appUserId,
    appSectionId: setting.appSectionId,
    setting: setting.setting?.trim() ? setting.setting : JSON.stringify(null),
    isActive: true,
    sortId: setting.sortId ?? 1,
  };
  const searchDefaults = parseOrderSearchDefaults(appUserSetting.setting);
  const filterOptions = await loadOrderFilterOptions(page, false);
  return { refreshRate: getOrderRefreshRate(appUserSetting.setting), searchDefaults, filterOptions, appUserId: user.appUserId, page, appUserSetting, notificationTypeId, options, selectedIds };
};

export const saveOrderSettings = async (
  draft: OrderSettingsDraft,
  selectedIds: number[],
  searchDefaults: OrderSearchDefaults = draft.searchDefaults,
  refreshRate?: OrderRefreshRate,
): Promise<void> => {
  if (!onlineStatus.get()) throw new Error('Connect to the internet to save your settings.');
  const user = await getUser();
  if (user?.appUserId !== draft.appUserId) throw new Error('Your session changed. Reopen Settings to continue.');

  const payload: SaveOrderSettingsPayload = {
    appUserSetting: { ...draft.appUserSetting, setting: serializeOrderSearchDefaults(draft.appUserSetting.setting, draft.page, searchDefaults, refreshRate) },
    notificationTypeId: draft.notificationTypeId,
    appUserEventIds: [...new Set(selectedIds)],
  };
  let savedSetting;
  try {
    const response = await API.postSaveOrderSettings(payload);
    const result = response.data;
    // not_updated describes unchanged search defaults, not the notification operation.
    if (response.status !== 200 || !result || result.message?.toLowerCase().includes('error')) {
      throw new Error('Your settings could not be saved. Please retry.');
    }
    savedSetting = result.data;
  } catch (error) {
    console.error('Failed to save order settings:', error);
    throw new Error('Your settings could not be saved. Please retry.');
  }
  try {
    await updateOrderSettings(draft.appUserId, orderSettingsKeys[draft.page], {
      appUserSetting: savedSetting,
      notificationTypeId: payload.notificationTypeId,
      appUserEventIds: payload.appUserEventIds,
    });
  } catch (error) {
    console.error('Failed to cache saved order settings:', error);
    throw new Error('Settings were saved online, but the local copy could not be updated. Please retry.');
  }
};
